import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { visuallyHidden } from '@mui/utils';
import FormattedMessage from '../../../../../components/TranslationWrapper';
import globalMessages from '../../../../../../config/globalMessages';
import FlowStatsList from '../../../../../../config/containers/FlowStats/FlowStatsList';
import { addModal } from '../../../../ModalManager/actions';
import { Context as LanguageContext } from '../../../../LanguageProvider';
import { PRODUCTION_STOP_REASONS_ENDPOINT, PRODUCTION_STOP_LOCATIONS_ENDPOINT } from './machineStatDataConstants';
import { PRODUCTION_STOP_COLOR, STATISTICS_THEME as T } from './statisticsOverlayConstants';
import { formatTooltipDate, getProductionStopReason, toLocalInput } from './statisticsOverlayHelpers';

const stopButtonSx = {
  border: '1px solid',
  borderColor: 'ledinekOrange.main',
  backgroundColor: 'ledinekOrange.main',
  color: 'text.primary',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': { borderColor: '#e2b000', backgroundColor: '#e2b000', color: 'text.primary' }
};

const stopCancelButtonSx = {
  ...stopButtonSx,
  backgroundColor: 'background.paper',
  borderColor: 'divider',
  '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.secondary', color: 'text.primary' }
};

const stopSelectMenuProps = { sx: { zIndex: 'calc(var(--z-index-modal-content) + 3)' } };

const formatStoppedTime = (minutes) => {
  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  const parts = [];
  if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (remainingMinutes || !hours) parts.push(`${remainingMinutes} minute${remainingMinutes === 1 ? '' : 's'}`);
  return parts.join(' ');
};

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return fallback;
};

function ProductionStopPresetForm({ kind, saving, error, onSave, onCancel }) {
  const [values, setValues] = useState({ en: '', fr: '', name: '', issue: 'fournier' });
  const fields =
    kind === 'reason'
      ? [
          { key: 'en', message: globalMessages.statisticsStopReasonEnglish },
          { key: 'fr', message: globalMessages.statisticsStopReasonFrench }
        ]
      : [{ key: 'name', message: globalMessages.statisticsStopNewLocation }];
  const valid = kind === 'reason' ? Boolean(values.en.trim() || values.fr.trim()) : Boolean(values.name.trim());
  return (
    <Box
      sx={{ display: 'grid', gap: 1 }}
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !saving) onSave(kind, kind === 'reason' ? { en: values.en.trim(), fr: values.fr.trim(), issue: values.issue } : { name: values.name.trim() });
      }}>
      {fields.map((field) => (
        <TextField
          key={field.key}
          size="small"
          label={<FormattedMessage {...field.message} />}
          value={values[field.key]}
          onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}
          disabled={saving}
          inputProps={kind === 'location' ? { maxLength: 150 } : undefined}
        />
      ))}
      {kind === 'reason' && (
        <>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            <FormattedMessage {...globalMessages.statisticsStopReasonLanguagesHint} />
          </Typography>
          <TextField
            select
            size="small"
            label={<FormattedMessage {...globalMessages.statisticsStopIssueLabel} />}
            value={values.issue}
            onChange={(event) => setValues((current) => ({ ...current, issue: event.target.value }))}
            disabled={saving}
            SelectProps={{ MenuProps: { sx: { zIndex: 'calc(var(--z-index-modal-content) + 5)' } } }}>
            <MenuItem value="fournier">
              <FormattedMessage {...globalMessages.statisticsStopIssueFournier} />
            </MenuItem>
            <MenuItem value="ledinek">
              <FormattedMessage {...globalMessages.statisticsStopIssueLedinek} />
            </MenuItem>
            <MenuItem value="other">
              <FormattedMessage {...globalMessages.statisticsStopIssueOther} />
            </MenuItem>
          </TextField>
        </>
      )}
      {error && (
        <Typography role="alert" sx={{ color: '#b91c1c', fontSize: 12 }}>
          {error}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button type="submit" size="small" variant="contained" disabled={!valid || saving} sx={stopButtonSx}>
          {saving && <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />}
          <FormattedMessage {...(kind === 'reason' ? globalMessages.statisticsStopSaveReason : globalMessages.statisticsStopSaveLocation)} />
        </Button>
        <Button type="button" size="small" variant="outlined" onClick={onCancel} disabled={saving} sx={stopCancelButtonSx}>
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
      </Box>
    </Box>
  );
}

export function ProductionStopDialog({ open, stop, dataset, productionStops, onClose, onSelectRange, canSelectRange }) {
  const { locale } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const [form, setForm] = useState({ start: '', duration: 1, reason: '', reason_preset: null, reason_translations: null, location: '', issue: 'fournier' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [presets, setPresets] = useState({ reasons: [], locations: [], loading: false, error: '' });
  const [newPreset, setNewPreset] = useState(null);
  const [presetSaving, setPresetSaving] = useState(false);
  const [presetError, setPresetError] = useState('');
  const presetsEnabled = FlowStatsList.productionStopPresets === true;
  const inputTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const busy = saving || presetSaving;
  const selectedReason = form.reason_preset ? presets.reasons.find((reason) => reason.id === form.reason_preset) ?? form.reason_translations : null;
  const visibleReason = getProductionStopReason({ ...form, reason_translations: selectedReason }, locale);

  useEffect(() => {
    if (!open) return;
    setForm(
      stop.draft ?? {
        start: toLocalInput(stop.started_at),
        duration: stop.duration_minutes ?? 1,
        reason: stop.reason ?? '',
        reason_preset: stop.reason_preset ?? null,
        reason_translations: stop.reason_translations ?? null,
        location: stop.location ?? '',
        issue: stop.issue ?? 'fournier'
      }
    );
    setError('');
    setNewPreset(null);
    setPresetError('');
  }, [open, stop]);

  useLayoutEffect(() => {
    if (!open || !presetsEnabled) return undefined;
    let cancelled = false;
    setPresets((current) => ({ ...current, loading: true, error: '' }));
    Promise.all([axios.get(PRODUCTION_STOP_REASONS_ENDPOINT), axios.get(PRODUCTION_STOP_LOCATIONS_ENDPOINT)])
      .then(([reasons, locations]) => {
        if (!cancelled) setPresets({ reasons: reasons.data, locations: locations.data, loading: false, error: '' });
      })
      .catch(() => {
        if (!cancelled) setPresets((current) => ({ ...current, loading: false, error: <FormattedMessage {...globalMessages.statisticsStopPresetsLoadFailed} /> }));
      });
    return () => {
      cancelled = true;
    };
  }, [open, presetsEnabled]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value, ...(field === 'reason' ? { reason_preset: null, reason_translations: null } : {}) }));
  const chooseReason = (reason) => setForm((current) => ({ ...current, reason: reason.en, reason_preset: reason.id, reason_translations: reason, issue: reason.issue ?? 'fournier' }));
  const createPreset = async (kind, values) => {
    setPresetSaving(true);
    setPresetError('');
    try {
      const response = await axios.post(kind === 'reason' ? PRODUCTION_STOP_REASONS_ENDPOINT : PRODUCTION_STOP_LOCATIONS_ENDPOINT, values);
      const key = kind === 'reason' ? 'reasons' : 'locations';
      setPresets((current) => ({ ...current, [key]: [...current[key], response.data] }));
      if (kind === 'reason') chooseReason(response.data);
      else setForm((current) => ({ ...current, location: response.data.name }));
      setNewPreset(null);
    } catch (failure) {
      setPresetError(errorMessage(failure, <FormattedMessage {...globalMessages.statisticsStopPresetSaveFailed} />));
    } finally {
      setPresetSaving(false);
    }
  };
  const deletePreset = async (kind, item) => {
    setPresetSaving(true);
    setPresets((current) => ({ ...current, error: '' }));
    try {
      await axios.delete(`${kind === 'reason' ? PRODUCTION_STOP_REASONS_ENDPOINT : PRODUCTION_STOP_LOCATIONS_ENDPOINT}${item.id}/`);
      const key = kind === 'reason' ? 'reasons' : 'locations';
      setPresets((current) => ({ ...current, [key]: current[key].filter((preset) => preset.id !== item.id) }));
      if (kind === 'reason') {
        setForm((current) => (current.reason_preset === item.id ? { ...current, reason: getProductionStopReason(current, locale), reason_preset: null, reason_translations: null } : current));
      }
      productionStops.refresh();
    } catch (failure) {
      setPresets((current) => ({
        ...current,
        error: errorMessage(failure, <FormattedMessage {...(kind === 'reason' ? globalMessages.statisticsStopReasonDeleteFailed : globalMessages.statisticsStopLocationDeleteFailed)} />)
      }));
    } finally {
      setPresetSaving(false);
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (busy || newPreset) return;
    const start = new Date(form.start);
    const duration = Number(form.duration);
    if (!Number.isFinite(start.getTime()) || toLocalInput(start) !== form.start || !Number.isFinite(duration) || duration <= 0 || !form.reason.trim()) {
      setError(<FormattedMessage {...globalMessages.statisticsStopInvalidFields} />);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await productionStops.save(stop.id, {
        area_identifier: dataset.key,
        started_at: start.toISOString(),
        duration_minutes: duration,
        reason: form.reason.trim(),
        reason_preset: form.reason_preset ?? null,
        location: form.location.trim(),
        input_timezone: inputTimezone,
        issue: form.issue
      });
      productionStops.refresh();
      onClose();
    } catch (failure) {
      setError(errorMessage(failure, <FormattedMessage {...globalMessages.statisticsStopSaveFailed} />));
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} disableEnforceFocus fullWidth maxWidth={presetsEnabled ? 'lg' : 'sm'} sx={{ zIndex: 'calc(var(--z-index-modal-content) + 2)' }}>
        <Box component="form" onSubmit={submit}>
          <DialogTitle sx={{ backgroundColor: 'ledinekOrange.main', color: 'text.primary', fontWeight: 700, mb: 1 }}>
            <FormattedMessage {...(stop?.id ? globalMessages.statisticsStopEditTitle : globalMessages.statisticsStopAddTitle)} values={{ area: dataset.label }} />
          </DialogTitle>
          <DialogContent sx={{ display: 'grid', gridTemplateColumns: presetsEnabled ? { xs: '1fr', md: 'minmax(0, 1fr) minmax(400px, 1fr)' } : '1fr', gap: 2, pt: '8px !important' }}>
            <Box sx={{ display: 'grid', alignContent: 'start', gap: 2, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  sx={{ flex: 1, minWidth: 0 }}
                  label={<FormattedMessage {...globalMessages.statisticsStopStartTime} values={{ timezone: inputTimezone }} />}
                  type="datetime-local"
                  value={form.start}
                  onChange={update('start')}
                  required
                  inputProps={{ step: 60 }}
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    form.start && productionStops.timeZone ? (
                      <FormattedMessage {...globalMessages.statisticsStopSiteTime} values={{ time: formatTooltipDate(form.start, productionStops.timeZone), timezone: productionStops.timeZone }} />
                    ) : (
                      ''
                    )
                  }
                />
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<ShowChartIcon fontSize="small" />}
                  onClick={() => onSelectRange(form)}
                  disabled={busy || Boolean(newPreset) || !canSelectRange}
                  sx={{ ...stopButtonSx, flexShrink: 0, whiteSpace: 'nowrap', height: 35 }}>
                  <FormattedMessage {...globalMessages.statisticsStopSelectGraph} />
                </Button>
              </Box>
              <TextField
                label={<FormattedMessage {...globalMessages.statisticsStopDuration} />}
                type="number"
                value={form.duration}
                onChange={update('duration')}
                required
                inputProps={{ min: 0, step: 'any' }}
              />
              <TextField label={<FormattedMessage {...globalMessages.statisticsStopReasonLabel} />} multiline minRows={2} value={visibleReason} onChange={update('reason')} required fullWidth />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                {presetsEnabled ? (
                  <TextField
                    select
                    label={<FormattedMessage {...globalMessages.statisticsStopLocationLabel} />}
                    value={form.location}
                    onChange={update('location')}
                    SelectProps={{ displayEmpty: true, MenuProps: stopSelectMenuProps }}
                    InputLabelProps={{ shrink: true }}>
                    <MenuItem value="">
                      <FormattedMessage {...globalMessages.statisticsStopNoLocation} />
                    </MenuItem>
                    {form.location && !presets.locations.some((location) => location.name === form.location) && <MenuItem value={form.location}>{form.location}</MenuItem>}
                    {presets.locations.map((location) => (
                      <MenuItem key={location.id} value={location.name}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField label={<FormattedMessage {...globalMessages.statisticsStopLocationLabel} />} value={form.location} onChange={update('location')} inputProps={{ maxLength: 150 }} />
                )}
                <TextField
                  select
                  label={<FormattedMessage {...globalMessages.statisticsStopIssueLabel} />}
                  value={form.issue}
                  onChange={update('issue')}
                  required
                  SelectProps={{ displayEmpty: true, MenuProps: stopSelectMenuProps }}>
                  <MenuItem value="fournier">
                    <FormattedMessage {...globalMessages.statisticsStopIssueFournier} />
                  </MenuItem>
                  <MenuItem value="ledinek">
                    <FormattedMessage {...globalMessages.statisticsStopIssueLedinek} />
                  </MenuItem>
                  <MenuItem value="other">
                    <FormattedMessage {...globalMessages.statisticsStopIssueOther} />
                  </MenuItem>
                </TextField>
              </Box>
              {error && (
                <Typography role="alert" sx={{ color: '#b91c1c', fontSize: 13 }}>
                  {error}
                </Typography>
              )}
            </Box>
            {presetsEnabled && (
              <Box
                component="aside"
                aria-labelledby="production-stop-reason-presets-label"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  height: { xs: 440, sm: 360 },
                  minHeight: 0,
                  borderLeft: { md: `1px solid ${T.border}` },
                  pl: { md: 1 }
                }}>
                {presets.error && (
                  <Typography role="alert" sx={{ color: '#b91c1c', fontSize: 12 }}>
                    {presets.error}
                  </Typography>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, flex: 1, minHeight: 0 }}>
                  {['reason', 'location'].map((kind) => {
                    const isReason = kind === 'reason';
                    const items = isReason ? presets.reasons : presets.locations;
                    const labelId = `production-stop-${kind}-presets-label`;
                    return (
                      <Box key={kind} component="section" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, gap: 1 }}>
                        <Typography id={labelId} sx={visuallyHidden}>
                          <FormattedMessage {...(isReason ? globalMessages.statisticsStopReasonPresets : globalMessages.statisticsStopLocations)} />
                        </Typography>
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setNewPreset(kind);
                            setPresetError('');
                          }}
                          disabled={busy || Boolean(newPreset) || presets.loading}
                          sx={stopButtonSx}>
                          <FormattedMessage {...(isReason ? globalMessages.statisticsStopAddReason : globalMessages.statisticsStopAddLocation)} />
                        </Button>
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper' }}>
                          {presets.loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                              <FormattedMessage {...globalMessages.statisticsStopPresetsLoading}>
                                {(label) => <CircularProgress size={24} sx={{ color: 'ledinekOrange.main' }} aria-label={label} />}
                              </FormattedMessage>
                            </Box>
                          ) : !items.length ? (
                            <Typography sx={{ p: 1, fontSize: 12, color: T.textMuted }}>
                              <FormattedMessage {...(isReason ? globalMessages.statisticsStopNoReasons : globalMessages.statisticsStopNoLocations)} />
                            </Typography>
                          ) : (
                            <List dense disablePadding aria-labelledby={labelId}>
                              {items.map((item) => {
                                const selected = isReason ? form.reason_preset === item.id : form.location === item.name;
                                return (
                                  <ListItem
                                    key={item.id}
                                    disablePadding
                                    divider
                                    sx={{
                                      backgroundColor: selected ? 'rgba(247, 195, 21, 0.18)' : undefined,
                                      boxShadow: selected ? 'inset 3px 0 #f7c315' : undefined,
                                      '&:hover': { backgroundColor: selected ? 'rgba(247, 195, 21, 0.24)' : 'rgba(247, 195, 21, 0.08)' },
                                      '&:last-child': { borderBottom: 0 }
                                    }}>
                                    <ListItemButton
                                      component="button"
                                      type="button"
                                      aria-pressed={selected}
                                      disabled={busy}
                                      onClick={() => (isReason ? chooseReason(item) : setForm((current) => ({ ...current, location: item.name })))}
                                      sx={{
                                        width: '100%',
                                        color: 'text.primary',
                                        '&:hover': { backgroundColor: 'transparent' }
                                      }}>
                                      <ListItemText
                                        primary={isReason ? item[locale.split('-')[0]] || item.en : item.name}
                                        primaryTypographyProps={{ sx: { fontSize: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } }}
                                      />
                                    </ListItemButton>
                                    <FormattedMessage
                                      {...(isReason ? globalMessages.statisticsStopDeleteReason : globalMessages.statisticsStopDeleteLocation)}
                                      values={{ name: isReason ? item[locale.split('-')[0]] || item.en : item.name }}>
                                      {(label) => (
                                        <IconButton
                                          type="button"
                                          size="small"
                                          color="error"
                                          aria-label={label}
                                          disabled={busy}
                                          onClick={() =>
                                            dispatch(
                                              addModal(
                                                'modal-confirmation',
                                                null,
                                                {
                                                  setting: {
                                                    buttonText: 'delete',
                                                    icon: 'warning',
                                                    modalText: (
                                                      <FormattedMessage
                                                        {...(isReason ? globalMessages.statisticsStopDeleteReasonConfirmation : globalMessages.statisticsStopDeleteLocationConfirmation)}
                                                        values={{ name: isReason ? item[locale.split('-')[0]] || item.en : item.name }}
                                                      />
                                                    )
                                                  }
                                                },
                                                () => deletePreset(kind, item)
                                              )
                                            )
                                          }
                                          sx={{ mx: 0.5 }}>
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </FormattedMessage>
                                  </ListItem>
                                );
                              })}
                            </List>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
            <Button type="submit" variant="contained" disabled={busy || Boolean(newPreset)} sx={stopButtonSx}>
              {saving ? (
                <FormattedMessage {...globalMessages.statisticsStopSaving}>{(label) => <CircularProgress color="inherit" size={18} aria-label={label} />}</FormattedMessage>
              ) : (
                <FormattedMessage {...globalMessages.statisticsStopSave} />
              )}
            </Button>
            <Button type="button" variant="outlined" onClick={onClose} disabled={busy} sx={stopCancelButtonSx}>
              <FormattedMessage {...globalMessages.cancel} />
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={open && Boolean(newPreset)} onClose={presetSaving ? undefined : () => setNewPreset(null)} fullWidth maxWidth="xs" sx={{ zIndex: 'calc(var(--z-index-modal-content) + 4)' }}>
        <DialogTitle sx={{ backgroundColor: 'ledinekOrange.main', color: 'text.primary', fontWeight: 700, mb: 1 }}>
          <FormattedMessage {...(newPreset === 'reason' ? globalMessages.statisticsStopAddReason : globalMessages.statisticsStopAddLocation)} />
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {newPreset && <ProductionStopPresetForm key={newPreset} kind={newPreset} saving={presetSaving} error={presetError} onSave={createPreset} onCancel={() => setNewPreset(null)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ProductionStopLog({ productionStops, loading, canEdit, canDelete, onEdit, onLeaveExclusions, onHover, unassignedStopMinutes = 0 }) {
  const { locale } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const stoppedMinutesByIssue = productionStops.stops.reduce(
    (totals, stop) => {
      const issue = stop.issue === 'fournier' || stop.issue === 'ledinek' ? stop.issue : 'other';
      totals[issue] += stop.duration_minutes;
      return totals;
    },
    { fournier: 0, other: 0, ledinek: unassignedStopMinutes }
  );
  const remove = async (stop) => {
    onLeaveExclusions();
    setDeleting(stop.id);
    setError('');
    try {
      await productionStops.remove(stop.id);
      productionStops.refresh();
    } catch (failure) {
      setError(errorMessage(failure, 'Could not delete the stop. Please try again.'));
    } finally {
      setDeleting(null);
    }
  };
  return (
    <Box sx={{ backgroundColor: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 1.5, p: 1.25, overflowX: 'auto', flexShrink: 0 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: PRODUCTION_STOP_COLOR }}>Recorded stops</Typography>
      {(error || productionStops.error) && (
        <Typography role="alert" sx={{ color: '#b91c1c', fontSize: 12 }}>
          {error || productionStops.error}
        </Typography>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} thickness={4.5} aria-label="Loading recorded stops" sx={{ color: PRODUCTION_STOP_COLOR }} />
        </Box>
      )}
      {!loading && !productionStops.error && !productionStops.stops.length && <Typography sx={{ fontSize: 12, color: T.textMuted, mt: 1 }}>No recorded stops in this timeframe.</Typography>}
      {!loading && productionStops.stops.length > 0 && (
        <Table size="small" aria-label="Recorded production stops" sx={{ '& th, & td': { fontSize: 12, py: 0.5, px: 1, borderColor: T.border } }}>
          <TableHead>
            <TableRow>
              <TableCell>Start</TableCell>
              <TableCell>Time stopped</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Issue</TableCell>
              {(canEdit || canDelete) && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {productionStops.stops.map((stop) => {
              const isUndefined = stop.reason.trim().toLowerCase() === 'undefined';
              return (
                <TableRow
                  key={stop.id}
                  onMouseEnter={() => onHover(stop.id)}
                  onMouseLeave={() => onHover(null)}
                  sx={{
                    backgroundColor: isUndefined ? '#fee2e2' : undefined,
                    '&:hover': { backgroundColor: isUndefined ? '#fecaca' : '#8b5cf614' }
                  }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTooltipDate(stop.started_at, productionStops.timeZone)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatStoppedTime(stop.duration_minutes)}</TableCell>
                  <TableCell>{stop.location || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{getProductionStopReason(stop, locale)}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{stop.issue}</TableCell>
                  {(canEdit || canDelete) && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {canEdit && (
                        <Button size="small" onClick={() => onEdit(stop)} disabled={deleting !== null}>
                          Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            dispatch(
                              addModal(
                                'modal-confirmation',
                                null,
                                {
                                  setting: {
                                    buttonText: 'delete',
                                    icon: 'warning',
                                    modalText: `Delete this recorded stop?\n${formatTooltipDate(stop.started_at, productionStops.timeZone)}\n${getProductionStopReason(stop, locale)}`
                                  }
                                },
                                () => remove(stop)
                              )
                            )
                          }
                          disabled={deleting !== null}
                          sx={{ ml: canEdit ? 1 : 0 }}>
                          {deleting === stop.id ? 'Deleting…' : 'Delete'}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {!loading && !productionStops.error && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1, pt: 1, borderTop: `1px solid ${T.border}` }}>
          {Object.entries(stoppedMinutesByIssue).map(([issue, minutes]) => (
            <Typography key={issue} sx={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>
              <Box component="span" sx={{ textTransform: 'capitalize' }}>
                {issue}
              </Box>
              : {formatStoppedTime(minutes)}
              {issue === 'ledinek' && (
                <>
                  {' '}
                  <FormattedMessage {...globalMessages.statisticsDefaultLedinekTime} values={{ time: formatStoppedTime(unassignedStopMinutes) }} />
                </>
              )}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
